import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import { Upload, PlusCircle, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { companyAtom } from "@/store/company";
import { useAtom } from "jotai";
import { prepareDisplayDate } from "@/lib/utils";

interface VehiclesConsumptionFormProps {
  consumptionId: number | null;
  vehicleId?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Vehicle {
  id: number;
  name: string;
  license_plate: string;
  company: number;
}

interface Facility {
  id: number;
  name: string;
  company: number;
}

// const UNIT_CHOICES = [
//   { value: "L", label: "vehicles.units.liters" },
//   { value: "kWh", label: "vehicles.units.kwh" },
//   { value: "kg", label: "vehicles.units.kg" },
//   { value: "km", label: "vehicles.units.km" },
// ];

const formSchema = z.object({
  custom_id: z.string().nullable().optional(),
  vehicle: z
    .number()
    .min(1, { message: "vehicles.consumption.error.vehicle_required" }),
  quantity: z
    .number()
    .min(0.01, { message: "Quantity must be greater than 0" }),
  invoice_amount: z.number().min(0),
  start_date: z.string().min(1, { message: "Start date is required" }),
  end_date: z.string().nullable().optional(),
  co2_eq: z.number().nullable().optional(),
  completed: z.boolean().default(false),
  driver: z.string().nullable().optional(),
  comments: z.string().nullable().optional(),
  document: z.instanceof(File).optional().nullable(),
  facilities: z
    .array(
      z.object({
        facility: z.number(),
        assigned: z.number().min(0).max(100),
      }),
    )
    .refine(
      (facilities) => {
        // Check if there are no duplicate facilities
        const facilityIds = facilities.map((f) => f.facility);
        return new Set(facilityIds).size === facilityIds.length;
      },
      {
        message: "Each facility can only be assigned once",
        path: ["facilities"],
      },
    )
    .refine(
      (facilities) => {
        // Check if the sum of assigned percentages is 100
        const sum = facilities.reduce((acc, f) => acc + f.assigned, 0);
        return sum === 100;
      },
      {
        message: "The sum of all percentages must be 100%",
        path: ["facilities"],
      },
    ),
});

type FormValues = z.infer<typeof formSchema>;

export function VehiclesConsumptionForm({
  consumptionId,
  vehicleId,
  onSuccess,
  onCancel,
}: VehiclesConsumptionFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [company] = useAtom(companyAtom);
  const [currentFileType, setCurrentFileType] = useState<string>("");
  const [isImagePreviewError, setIsImagePreviewError] = useState(false);
  const { token } = useAuth();

  console.log("company in VehiclesConsumptionForm", company);

  const userString = localStorage.getItem("user");
  const user = userString ? JSON.parse(userString) : null;

  // Check if user is admin
  const isAdmin = user?.rol === "admin";

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      custom_id: "",
      vehicle: vehicleId || 0,
      quantity: 0,
      invoice_amount: 0,
      start_date: format(new Date(), "yyyy-MM-dd"),
      end_date: format(new Date(), "yyyy-MM-dd"),
      co2_eq: 0,
      completed: false,
      driver: "",
      comments: "",
      document: undefined,
      facilities: [
        {
          facility: 0, // Will be updated when facilities are loaded
          assigned: 100,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "facilities",
  });

  // Function to add a new facility
  const addFacility = () => {
    // Calculate remaining percentage
    const currentTotal = form
      .getValues()
      .facilities.reduce((sum, facility) => sum + (facility.assigned || 0), 0);

    const remainingPercentage = Math.max(0, 100 - currentTotal);

    append({
      facility: 0, // Default to 0, will be updated by user
      assigned: remainingPercentage,
    });
  };

  // Function to handle removing a facility
  const handleRemoveFacility = (index: number) => {
    if (fields.length > 1) {
      // Get the percentage of the facility being removed
      const removedPercentage =
        form.getValues().facilities[index].assigned || 0;
      remove(index);

      // Redistribute the percentage to the first facility
      if (removedPercentage > 0 && fields.length > 0) {
        const facilities = form.getValues().facilities;
        const firstFacilityIndex = 0;
        const newPercentage =
          (facilities[firstFacilityIndex].assigned || 0) + removedPercentage;

        form.setValue(
          `facilities.${firstFacilityIndex}.assigned`,
          newPercentage,
        );
      }
    } else {
      toast.error(t("vehicles.consumption.error.min_one_facility"));
    }
  };

  // Function to check if a facility is already selected in another dropdown
  const isFacilitySelected = (facilityId: number, currentIndex: number) => {
    return form
      .getValues()
      .facilities.some(
        (f, idx) => idx !== currentIndex && f.facility === facilityId,
      );
  };

  // Fetch vehicles
  const { data: vehicles, isLoading: isLoadingVehicles } = useQuery<Vehicle[]>({
    queryKey: ["vehicles"],
    queryFn: async () => {
      let url = "/vehicles/?limit=9999";
      if (company) {
        url = `/vehicles/?company=${company.id}&limit=9999`;
      }
      const response = await fetch(getApiUrl(url), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch vehicles");
      }
      const res = await response.json();
      return res.results;
    },
    enabled: isAdmin || !vehicleId, // Only fetch all vehicles if admin or no vehicleId provided
  });

  // Fetch facilities
  const { data: facilities, isLoading: isLoadingFacilities } = useQuery<
    Facility[]
  >({
    queryKey: ["facilities"],
    queryFn: async () => {
      let url = "/facilities/?limit=9999";
      if (!isAdmin && user?.company) {
        url = `/facilities/?company=${user.company.id}&limit=9999`;
      }
      const response = await fetch(getApiUrl(url), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch facilities");
      }
      const res = await response.json();
      return res.results;
    },
  });

  // Fetch consumption data if editing
  const { data: consumption, isLoading: isLoadingConsumption } = useQuery({
    queryKey: ["consumption", consumptionId],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(`/consumptions/${consumptionId}/`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch consumption");
      }
      const res = await response.json();
      return res;
    },
    enabled: !!consumptionId,
  });

  // Verify if all necessary queries have finished loading
  const isLoading =
    isLoadingVehicles ||
    isLoadingFacilities ||
    (consumptionId && isLoadingConsumption);

  // Load consumption data when editing
  useEffect(() => {
    if (consumptionId) {
      if (consumption && !isLoading) {
        // Format the data for the form
        form.reset({
          vehicle: consumption.vehicle.id,
          quantity: +consumption.quantity,
          invoice_amount: +consumption.invoice_amount,
          start_date: consumption.start_date,
          end_date: consumption.end_date || undefined,
          completed: consumption.completed,
          driver: consumption.driver || "",
          comments: consumption.comments || "",
          document: undefined,
          // Map facilities from the API response to the form format
          facilities:
            Array.isArray(consumption.facilities) &&
            consumption.facilities.length > 0
              ? consumption.facilities.map(
                  (f: { facility: { id: number }; assigned: number }) => ({
                    facility: f.facility.id,
                    assigned: f.assigned,
                  }),
                )
              : facilities && facilities.length > 0
                ? [{ facility: facilities[0].id, assigned: 100 }]
                : [{ facility: 0, assigned: 100 }],
        });

        // Handle document
        if (consumption.document_url) {
          // URL already comes with authentication parameters from backend
          console.log("Document URL from backend:", consumption.document_url);
          setDocumentUrl(consumption.document_url);
          // Detect file type from URL extension for proper preview
          const url = consumption.document_url.toLowerCase();
          if (
            url.endsWith(".jpg") ||
            url.endsWith(".jpeg") ||
            url.includes(".jpg?") ||
            url.includes(".jpeg?") ||
            url.endsWith(".png") ||
            url.includes(".png?") ||
            url.endsWith(".webp") ||
            url.includes(".webp?") ||
            url.includes("jpg") ||
            url.includes("jpeg") ||
            url.includes("png")
          ) {
            console.log("Setting image type for preview");
            setCurrentFileType("image/jpeg"); // Default to image type for preview
          } else if (
            url.endsWith(".pdf") ||
            url.includes(".pdf?") ||
            url.includes("pdf")
          ) {
            console.log("Setting PDF type for preview");
            setCurrentFileType("application/pdf");
          } else {
            // Try to detect from content-type or default to image for preview
            console.log(
              "Could not detect file type from URL, defaulting to image",
            );
            setCurrentFileType("image/jpeg");
          }
        }

        setIsImagePreviewError(false);
      } else if (vehicleId && vehicles) {
        form.setValue("vehicle", vehicleId);

        // Set first facility if available when creating a new record
        if (facilities && facilities.length > 0) {
          form.setValue("facilities.0.facility", facilities[0].id);
        }
      }
    } else {
      // Caso de creación: reset a valores por defecto
      form.reset({
        vehicle: undefined,
        quantity: 0,
        invoice_amount: 0,
        start_date: "",
        end_date: undefined,
        completed: false,
        driver: "",
        comments: "",
        document: undefined,
        facilities: [
          {
            facility: undefined,
            assigned: 100,
          },
        ],
      });
    }
  }, [
    consumption,
    consumptionId,
    vehicleId,
    vehicles,
    facilities,
    form,
    isLoading,
  ]);

  // Handle file validation
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      let detectedType = file.type || "";
      if (!detectedType && file.name) {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext === "jpg" || ext === "jpeg") detectedType = "image/jpeg";
        else if (ext === "png") detectedType = "image/png";
        else if (ext === "webp") detectedType = "image/webp";
        else if (ext === "pdf") detectedType = "application/pdf";
      }

      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/jpg",
        "image/*",
      ];

      const isAllowed = allowedTypes.some((type) => {
        if (type === "image/*") {
          return detectedType.startsWith("image/");
        }
        return detectedType === type;
      });

      if (!isAllowed) {
        toast.error(t("vehicles.consumption.error.pdf_or_image_only"));
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error(t("vehicles.consumption.error.file_too_large"));
        return;
      }

      if (documentUrl && documentUrl.startsWith("blob:")) {
        URL.revokeObjectURL(documentUrl);
      }

      const newUrl = URL.createObjectURL(file);
      setDocumentUrl(newUrl);
      setCurrentFileType(detectedType);
      setIsImagePreviewError(false);
      form.setValue("document", file, { shouldValidate: true });
    } catch (error) {
      console.error("Error handling file change:", error);
      toast.error(t("common.error.unknown"));
    }
  };

  // Create consumption mutation
  const createConsumption = useMutation({
    mutationFn: async (data: FormValues) => {
      // Create a FormData object to handle file upload
      const formData = new FormData();

      // Add all form fields to FormData
      Object.entries(data).forEach(([key, value]) => {
        if (key === "facilities" && Array.isArray(value)) {
          // For each facility, add as a separate indexed field
          value.forEach(
            (
              facility: { facility: number; assigned: number },
              index: number,
            ) => {
              formData.append(
                `facilities[${index}]`,
                JSON.stringify({
                  facility: facility.facility,
                  assigned: facility.assigned,
                }),
              );
            },
          );
        } else if (key === "document" && value instanceof File) {
          // Add file to FormData
          formData.append("document", value);
        } else if (value !== null && value !== undefined) {
          // Add other fields
          formData.append(key, value.toString());
        }
      });

      const response = await fetch(getApiUrl("/consumptions/"), {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to create consumption");
      }
      const res = await response.json();
      return res;
    },
    onSuccess: () => {
      toast.success(t("common.success.created"));
      queryClient.invalidateQueries({ queryKey: ["consumption-facilities"] });
      onSuccess();
    },
    onError: (error: any) => {
      console.error("Error creating consumption:", error);
      toast.error(t("common.errors.create"));
    },
  });

  // Update consumption mutation
  const updateConsumption = useMutation({
    mutationFn: async (data: FormValues) => {
      console.log("updateConsumption", data);

      // Create a FormData object to handle file upload
      const formData = new FormData();

      // Extract facilities and document to process separately
      const { facilities, document, ...restData } = data;

      // Add all fields except facilities and document
      Object.entries(restData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          // Add other fields
          formData.append(key, value.toString());
        }
      });

      // Add document if it exists
      if (document instanceof File) {
        formData.append("document", document);
      }

      // Add facilities as a single JSON string
      if (facilities && facilities.length > 0) {
        // Convert facilities array to a JSON string
        const facilitiesJson = JSON.stringify(facilities);
        formData.append("facilities", facilitiesJson);
      }

      const response = await fetch(
        getApiUrl(`/consumptions/${consumptionId}/`),
        {
          method: "PATCH",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to update consumption");
      }
      const res = await response.json();
      return res;
    },
    onSuccess: () => {
      toast.success(t("common.success.updated"));
      queryClient.invalidateQueries({ queryKey: ["consumption-facilities"] });
      onSuccess();
    },
    onError: (error: any) => {
      console.error("Error updating consumption:", error);
      toast.error(t("common.errors.update"));
    },
  });

  // Form submission handler
  const onSubmit = (values: FormValues) => {
    if (fileError) return;

    // Check if facilities exist
    if (!values.facilities || values.facilities.length === 0) {
      toast.error(t("vehicles.consumption.error.min_one_facility"));
      return;
    }

    // Validate that all facilities have a valid facility ID
    const invalidFacility = values.facilities.some(
      (f) => !f.facility || f.facility === 0,
    );
    if (invalidFacility) {
      toast.error(t("vehicles.consumption.error.invalid_facility"));
      return;
    }

    // Validate that percentages sum to 100
    const totalPercentage = values.facilities.reduce(
      (sum, facility) => sum + facility.assigned,
      0,
    );
    if (Math.abs(totalPercentage - 100) > 0.01) {
      // Allow for small floating point errors
      toast.error(t("vehicles.consumption.error.percentage_sum"));
      return;
    }

    // Check for duplicate facilities
    const facilityIds = values.facilities.map((f) => f.facility);
    const uniqueFacilityIds = new Set(facilityIds);
    if (uniqueFacilityIds.size !== facilityIds.length) {
      toast.error(t("vehicles.consumption.error.duplicate_facility"));
      return;
    }

    // Submit the form data
    if (consumptionId) {
      updateConsumption.mutate(values);
    } else {
      createConsumption.mutate(values);
    }
  };

  // Show loading spinner while data is being fetched
  const isFormLoading =
    isLoadingVehicles ||
    isLoadingFacilities ||
    (consumptionId && isLoadingConsumption);

  if (isFormLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="h-full flex flex-col"
      >
        <div className="flex-1 flex flex-col md:flex-row overflow-y-auto">
          {/* Document Preview/Upload Section */}
          <div className="md:w-1/2 p-4 border-r">
            <div className="flex flex-col h-full">
              <h3 className="text-lg font-medium mb-4">
                {t("vehicles.consumption.fields.document")}
              </h3>

              {vehicleId && isLoadingVehicles ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                  <p className="text-muted-foreground">{t("common.loading")}</p>
                </div>
              ) : documentUrl ? (
                <div className="flex flex-col h-full">
                  <div className="flex flex-1 items-center justify-center min-h-[400px] border rounded-md overflow-hidden bg-white">
                    {/* Preview PDF or image */}
                    {(() => {
                      console.log(
                        "Rendering preview with type:",
                        currentFileType,
                        "URL:",
                        documentUrl,
                      );
                      if (currentFileType.startsWith("image/") && documentUrl) {
                        return (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-full h-full flex items-center justify-center">
                              {isImagePreviewError ? (
                                <div className="p-4 text-center">
                                  <p>{t("common.nopreviewload")}</p>
                                  <a
                                    href={documentUrl || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary underline"
                                  >
                                    {t("common.opennewtab")}
                                  </a>
                                </div>
                              ) : (
                                <img
                                  src={documentUrl || ""}
                                  alt={t(
                                    "vehicles.consumption.fields.document",
                                  )}
                                  className="object-contain max-w-full max-h-full"
                                />
                              )}
                            </div>
                          </div>
                        );
                      } else if (documentUrl) {
                        // Default to PDF preview
                        return (
                          <object
                            data={documentUrl}
                            type="application/pdf"
                            width="100%"
                            height="100%"
                            className="min-h-[400px]"
                          >
                            <div className="p-4">
                              <p>
                                {t("vehicles.consumption.pdf_not_supported")}
                              </p>
                              <a
                                href={documentUrl || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline"
                              >
                                {t("vehicles.consumption.download_document")}
                              </a>
                            </div>
                          </object>
                        );
                      }
                    })()}
                  </div>

                  <div className="mt-4">
                    <label htmlFor="document-upload" className="cursor-pointer">
                      <div className="flex items-center justify-center p-4 border-2 border-dashed rounded-md hover:bg-muted transition-colors">
                        <Upload className="h-6 w-6 mr-2" />
                        <span>{t("combustion.replace_document")}</span>
                      </div>
                      <input
                        id="document-upload"
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col justify-center items-center h-full">
                  <label
                    htmlFor="document-upload"
                    className="cursor-pointer w-full"
                  >
                    <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-md hover:bg-muted transition-colors min-h-[400px]">
                      <Upload className="h-12 w-12 mb-4 text-muted-foreground" />
                      <p className="text-lg font-medium">
                        {t("vehicles.consumption.upload_document_any")}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {t("vehicles.consumption.pdf_or_image_only")}
                      </p>
                    </div>
                    <input
                      id="document-upload"
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Form Fields Section */}
          <div className="md:w-1/2 flex-1 flex flex-col space-y-4 overflow-y-auto p-4">
            <FormField
              control={form.control}
              name="vehicle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("vehicles.consumption.fields.vehicle")}
                  </FormLabel>
                  <Select
                    value={field.value ? field.value.toString() : undefined}
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    disabled={!!vehicleId}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("vehicles.consumption.select_vehicle")}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {vehicles?.map((vehicle) => (
                        <SelectItem
                          key={vehicle.id}
                          value={vehicle.id.toString()}
                        >
                          {vehicle.license_plate}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* <FormField
              control={form.control}
              name="custom_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("vehicles.consumption.fields.custom_id")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            <div className="flex gap-4 items-end">
              <div className="flex gap-1 items-end flex-1">
                <FormField
                  control={form.control}
                  name="quantity"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>
                        {t("vehicles.consumption.fields.quantity")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value) || 0)
                          }
                          value={field.value || 0}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>l.</div>
              </div>

              <FormField
                control={form.control}
                name="invoice_amount"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t("combustion.fields.invoice_amount")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t("vehicles.consumption.fields.unit")}
                    </FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t("vehicles.consumption.select_unit")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {UNIT_CHOICES.map((unit) => (
                            <SelectItem key={unit.value} value={unit.value}>
                              {t(unit.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
            </div>

            {/* Agrupar Start Date y End Date en una fila */}
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t("vehicles.consumption.fields.start_date")}
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        date={
                          field.value
                            ? prepareDisplayDate(field.value)
                            : undefined
                        }
                        setDate={(date: Date | null) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = date.getMonth();
                            const day = date.getDate();
                            const correctedDate = new Date(
                              Date.UTC(year, month, day),
                            );
                            field.onChange(
                              correctedDate.toISOString().split("T")[0],
                            );
                          } else {
                            field.onChange(null);
                          }
                        }}
                        disableFutureDates={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t("vehicles.consumption.fields.end_date")}
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        date={
                          field.value
                            ? prepareDisplayDate(field.value)
                            : undefined
                        }
                        setDate={(date: Date | null) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = date.getMonth();
                            const day = date.getDate();
                            const correctedDate = new Date(
                              Date.UTC(year, month, day),
                            );
                            field.onChange(
                              correctedDate.toISOString().split("T")[0],
                            );
                          } else {
                            field.onChange(null);
                          }
                        }}
                        disableFutureDates={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">
                  {t("vehicles.consumption.fields.facilities")}
                </h3>
                <Button
                  type="button"
                  variant="gradient"
                  size="sm"
                  onClick={addFacility}
                  className="flex items-center gap-1"
                >
                  <PlusCircle className="h-4 w-4" />
                  {t("vehicles.consumption.add_facility")}
                </Button>
              </div>

              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex gap-4 items-end border p-3 rounded-md"
                >
                  <div className="flex-grow">
                    <FormField
                      control={form.control}
                      name={`facilities.${index}.facility`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("vehicles.consumption.fields.facility")}
                          </FormLabel>
                          <Select
                            value={
                              field.value ? field.value.toString() : undefined
                            }
                            onValueChange={(value) =>
                              field.onChange(parseInt(value))
                            }
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={t(
                                    "vehicles.consumption.select_facility",
                                  )}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Array.isArray(facilities)
                                ? facilities.map((facility) => (
                                    <SelectItem
                                      key={facility.id}
                                      value={facility.id.toString()}
                                      disabled={isFacilitySelected(
                                        facility.id,
                                        index,
                                      )}
                                    >
                                      {facility.name}
                                    </SelectItem>
                                  ))
                                : null}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="w-24">
                    <FormField
                      control={form.control}
                      name={`facilities.${index}.assigned`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("vehicles.consumption.fields.percentage")}
                          </FormLabel>
                          <div className="flex items-center">
                            <FormControl>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                {...field}
                                onChange={(e) =>
                                  field.onChange(parseInt(e.target.value) || 0)
                                }
                              />
                            </FormControl>
                            <span className="ml-2">%</span>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="self-end"
                    onClick={() => handleRemoveFacility(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              {/* Show validation errors for the facilities array */}
              {form.formState.errors.facilities && (
                <div className="mt-2 p-2 border border-destructive rounded-md bg-destructive/10">
                  <p className="text-sm font-medium text-destructive">
                    {form.formState.errors.facilities.message ||
                      t("vehicles.consumption.error.percentage_sum")}
                  </p>
                </div>
              )}
            </div>

            <FormField
              control={form.control}
              name="driver"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("vehicles.consumption.fields.driver")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="comments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("vehicles.consumption.fields.comments")}
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2 border-t bg-white p-4 sticky bottom-0">
          <Button variant="outline" type="button" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="gradient"
            type="submit"
            disabled={
              createConsumption.isPending || updateConsumption.isPending
            }
          >
            {consumptionId ? t("common.update") : t("common.create")}
            {(createConsumption.isPending || updateConsumption.isPending) && (
              <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
